import { Link } from "react-router-dom";
import { PhoneCall, Search, CheckCircle2, Clock, DollarSign } from "lucide-react";
import { BUSINESS_PHONE_FORMATTED, OWNER_FIRST_NAME } from "@/lib/constants";

const steps = [
    {
        Icon: PhoneCall,
        n: 1,
        title: "Contact me",
        blurb: `Call or text ${BUSINESS_PHONE_FORMATTED}, or submit the quick form.`,
    },
    {
        Icon: Search,
        n: 2,
        title: "I check your options",
        blurb: `${OWNER_FIRST_NAME} reviews the plans available in your area and service type.`,
    },
    {
        Icon: CheckCircle2,
        n: 3,
        title: "You decide",
        blurb: "No pressure. You choose what works best for your household or shop.",
    },
];

export default function WhyOverpay() {
    return (
        <section className="relative" data-testid="why-overpay">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
                <div className="grid lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-6">
                        <span className="chip">Why overpay?</span>
                        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
                            Same service.
                            <br />
                            <span className="accent-gradient-text">Lower rates.</span>
                        </h2>
                        <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
                            Your lights, phone, and internet don't change when you shop smart. The
                            bill does. In just a few minutes we'll see what options really exist
                            in your area - and whether any beat what you have now.
                        </p>

                        <div className="mt-6 grid grid-cols-3 gap-3">
                            <div className="card-surface p-4">
                                <DollarSign className="w-5 h-5 text-emerald-300" />
                                <div className="mt-2 text-xs text-slate-400">Lower monthly</div>
                                <div className="text-sm font-semibold text-white">Bills</div>
                            </div>
                            <div className="card-surface p-4">
                                <Clock className="w-5 h-5 text-emerald-300" />
                                <div className="mt-2 text-xs text-slate-400">Takes just</div>
                                <div className="text-sm font-semibold text-white">A few minutes</div>
                            </div>
                            <div className="card-surface p-4">
                                <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                                <div className="mt-2 text-xs text-slate-400">No pressure</div>
                                <div className="text-sm font-semibold text-white">You decide</div>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/get-started" className="btn-primary" data-testid="why-cta-start">
                                Check my options
                            </Link>
                            <a href={`sms:${BUSINESS_PHONE_FORMATTED.replace(/\D/g, "")}?&body=Text me my options`} className="btn-ghost" data-testid="why-cta-text">
                                Text me instead
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-6">
                        <div className="card-surface p-6 sm:p-8">
                            <div className="mono text-xs text-slate-400 uppercase tracking-widest">
                                It only takes a few minutes
                            </div>
                            <div className="mt-5 space-y-5">
                                {steps.map((s) => (
                                    <div key={s.n} className="flex gap-4" data-testid={`why-step-${s.n}`}>
                                        <div className="shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-emerald-500 text-emerald-950 font-black inline-flex items-center justify-center">
                                                {s.n}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <s.Icon className="w-4 h-4 text-emerald-300" />
                                                <h3 className="text-white font-semibold">{s.title}</h3>
                                            </div>
                                            <p className="mt-1 text-sm text-slate-400 leading-relaxed">{s.blurb}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-6 text-xs text-slate-500 leading-relaxed">
                                Independent representative. We don't display or promise specific
                                rates - we help you request a real comparison for your area.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
